import React, {
  useCallback,
  useMemo,
} from 'react';
import clsx from 'clsx';
import {
  Controller,
  useFormContext,
} from 'react-hook-form';
import {
  isEmptyString,
} from '@populateui/utils';
import ZipCodeTextField from 'core/components/ZipCodeTextField';
import {
  AddressDTO,
} from 'dtos';
import AddressSuggestion from 'pages/Dashboard/components/AddressSuggestion';
import USStateSelect from 'pages/Dashboard/components/USStateSelect';
import {
  formatPlace,
} from 'utils/misc';
import {
  getAddressString,
} from 'utils/string';
import FormTextField from 'views/EMR/components/FillableForm/components/FormTextField';
import {
  FieldComponentProps,
} from 'views/EMR/components/FillableForm/types';

export default function USAddress({
  field: formField,
}: FieldComponentProps) {
  const {
    control,
    setValue,
    trigger,
    watch,
  } = useFormContext();
  const fieldId = formField?.id ?? '';
  const valueBase = `${fieldId}.value`;
  const street = watch(`${valueBase}.street`);
  const unit = watch(`${valueBase}.unit`);
  const city = watch(`${valueBase}.city`);
  const state = watch(`${valueBase}.state`);
  const zipCode = watch(`${valueBase}.zipCode`);

  const addressValue = useMemo<AddressDTO>(() => ({
    street: street ?? '',
    unit: unit ?? '',
    city: city ?? '',
    state: state ?? '',
    zipCode: zipCode ?? '',
  }), [city, state, street, unit, zipCode]);

  const suggestionValue = useMemo(
    () => (getAddressString(addressValue) === '—' ? null : formatPlace(addressValue).place),
    [addressValue],
  );

  const handleAddressSuggestion = useCallback(async (address: AddressDTO) => {
    const nextAddress = {
      ...address,
      state: address?.state?.toUpperCase() ?? '',
      zipCode: address?.shortZipCode ?? address?.zipCode ?? '',
    };

    setValue(`${valueBase}.street`, nextAddress.street ?? '', { shouldDirty: true, shouldTouch: true, shouldValidate: true });
    setValue(`${valueBase}.city`, nextAddress.city ?? '', { shouldDirty: true, shouldTouch: true, shouldValidate: true });
    setValue(`${valueBase}.state`, nextAddress.state ?? '', { shouldDirty: true, shouldTouch: true, shouldValidate: true });
    setValue(`${valueBase}.zipCode`, nextAddress.zipCode ?? '', { shouldDirty: true, shouldTouch: true, shouldValidate: true });

    await trigger([
      `${valueBase}.street`,
      `${valueBase}.city`,
      `${valueBase}.state`,
      `${valueBase}.zipCode`,
    ]);
  }, [setValue, trigger, valueBase]);

  return (
    <div className="space-y-3 w-full">
      <div className="w-full">
        <AddressSuggestion
          className="!w-full"
          value={suggestionValue}
          onChange={handleAddressSuggestion}
        />
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-12">
        <Controller
          name={`${valueBase}.street`}
          control={control}
          rules={{ required: true }}
          render={({ field, fieldState: { invalid } }) => (
            <div className={clsx('sm:col-span-4', { 'has-error': invalid })}>
              <FormTextField
                {...field}
                id={`${formField?.id ?? ''}-street`}
                autoFocus
                fullWidth
                placeholder="Street"
                slotProps={{ htmlInput: { maxLength: 64 } }}
              />
            </div>
          )}
        />
        <Controller
          name={`${valueBase}.unit`}
          control={control}
          render={({ field }) => (
            <div className="sm:col-span-2">
              <FormTextField
                {...field}
                id={`${formField?.id ?? ''}-unit`}
                fullWidth
                placeholder="Unit, Apt, Suite"
                slotProps={{ htmlInput: { maxLength: 32 } }}
              />
            </div>
          )}
        />
        <Controller
          name={`${valueBase}.city`}
          control={control}
          rules={{ required: true }}
          render={({ field, fieldState: { invalid } }) => (
            <div className={clsx('sm:col-span-2', { 'has-error': invalid })}>
              <FormTextField
                {...field}
                id={`${formField?.id ?? ''}-city`}
                fullWidth
                placeholder="City"
                slotProps={{ htmlInput: { maxLength: 64 } }}
              />
            </div>
          )}
        />
        <Controller
          name={`${valueBase}.state`}
          control={control}
          rules={{
            required: true,
            validate: (v) => isEmptyString(v) || /^[A-Za-z]{2}$/.test((v ?? '').toString()),
          }}
          render={({ field: { value, onChange, onBlur }, fieldState: { invalid } }) => (
            <div className={clsx('sm:col-span-2', { 'has-error': invalid })}>
              <USStateSelect
                className="w-full"
                value={value as string}
                onChange={onChange}
                onBlur={onBlur}
              />
            </div>
          )}
        />
        <Controller
          name={`${valueBase}.zipCode`}
          control={control}
          rules={{
            required: true,
            validate: (v) => /^\d{5}$/.test((v ?? '').toString()),
          }}
          render={({ field, fieldState: { invalid } }) => (
            <div className={clsx('sm:col-span-2', { 'has-error': invalid })}>
              <ZipCodeTextField
                {...field}
                id={`${formField?.id ?? ''}-zipCode`}
                className="w-full"
                inputProps={{ placeholder: 'ZIP Code' }}
              />
            </div>
          )}
        />
      </div>
    </div>
  );
}
